import { RolodexIndication } from 'features/indicationTriage'
import { getStringTag } from 'features/libra/helpers/getStringTag'
import { ctpEntryToLayer } from 'features/libra/stepComponents/ffs/ffsCtpMarkup'
import { indicationToRectLayer } from 'features/libra/stepComponents/ffs/ffsIndicationMarkup'
import { isFfsLtaPathMarkupLayer } from 'features/libra/stepComponents/ffs/ffsLtaPathMarkup'
import { rulerEntryToLayer } from 'features/libra/stepComponents/ffs/ffsRulerMarkup'
import { useFfsWorkspaceState } from 'features/libra/workspace/ffs/FfsWorkspaceStateContext'
import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import { useCallback, useEffect, useMemo } from 'react'
import {
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoEntry,
  GeckoMarkupLayer,
  GeckoMeasurementUnit,
  RolodexEntryType,
} from 'types'

interface UseFfsMarkupLayersArgs {
  indications: ReadonlyArray<RolodexIndication>
  ltaSetId: string | undefined
  component: GeckoComponent | undefined
  inspectionId: string | undefined
  selectedIndicationId?: string | null
}

interface UseFfsMarkupLayersResult {
  /** Refreshes every Rolodex query owned by this hook. */
  forceRefresh: () => Promise<unknown>
}

interface BuildFfsMarkupLayersArgs {
  indications: ReadonlyArray<RolodexIndication>
  selectedIndicationId?: string | null
  ctps: ReadonlyArray<GeckoEntry>
  rulers: ReadonlyArray<GeckoEntry>
  cdfsById: ReadonlyMap<string, GeckoComponentDataFrame>
  primaryCdf: GeckoComponentDataFrame | undefined
  component: GeckoComponent | undefined
  flawMarkupLayers: ReadonlyArray<GeckoMarkupLayer>
}

const lengthUnit = GeckoMeasurementUnit.Millimeters

const classificationFor = (indication: RolodexIndication): string | undefined =>
  indication.classificationInfo?.classification

export function buildFfsMarkupLayers({
  indications,
  selectedIndicationId,
  ctps,
  rulers,
  cdfsById,
  primaryCdf,
  component,
  flawMarkupLayers,
}: BuildFfsMarkupLayersArgs): GeckoMarkupLayer[] {
  const cdfByIndicationId = new Map<
    string,
    GeckoComponentDataFrame | undefined
  >()
  for (const ctp of ctps) {
    const indicationId = ctp.tags.indication as string | undefined
    if (!indicationId) continue
    const ctpCdfId = ctp.tags['component-data-frame'] as string | undefined
    cdfByIndicationId.set(
      indicationId,
      (ctpCdfId ? cdfsById.get(ctpCdfId) : undefined) ?? primaryCdf
    )
  }

  const resolveCtpCdf = (ctpEntry: { tags: { [k: string]: unknown } }) => {
    const ctpCdfId = ctpEntry.tags['component-data-frame'] as string | undefined
    return (ctpCdfId ? cdfsById.get(ctpCdfId) : undefined) ?? primaryCdf
  }

  return [
    ...flawMarkupLayers,
    ...indications.flatMap((i) =>
      indicationToRectLayer(
        i,
        classificationFor(i),
        i.id === selectedIndicationId
      )
    ),
    ...ctps.flatMap((ctp, i) =>
      ctpEntryToLayer(ctp, resolveCtpCdf(ctp), i + 1)
    ),
    ...rulers.flatMap((r) => {
      const indicationId = r.tags.indication ?? undefined
      const cdf = indicationId
        ? (cdfByIndicationId.get(indicationId) ?? primaryCdf)
        : primaryCdf
      return cdf && component
        ? rulerEntryToLayer(r, cdf, component, lengthUnit)
        : []
    }),
  ]
}

export function useFfsMarkupLayers({
  indications,
  ltaSetId,
  component,
  inspectionId,
  selectedIndicationId,
}: UseFfsMarkupLayersArgs): UseFfsMarkupLayersResult {
  const { primaryCdf, setFfsMarkupLayers } = useFfsWorkspaceState()

  const indicationIds = useMemo(
    () => indications.map((i) => i.id),
    [indications]
  )

  const { entries: ctpEntries, forceRefresh: refreshCtps } =
    useRolodexEntriesWithSyncedRefreshes(
      indicationIds.length > 0
        ? {
            type: RolodexEntryType.FFS_CRITICAL_THICKNESS_PROFILE,
            tags: { [RolodexEntryType.INDICATION]: indicationIds },
          }
        : undefined
    )
  const ctps = ctpEntries

  const { entries: rulerEntries, forceRefresh: refreshRulers } =
    useRolodexEntriesWithSyncedRefreshes(
      indicationIds.length > 0
        ? {
            type: RolodexEntryType.RULER,
            tags: { [RolodexEntryType.INDICATION]: indicationIds },
          }
        : undefined
    )
  const rulers = rulerEntries

  const ctpCdfIds = useMemo(
    () =>
      Array.from(
        new Set(
          ctps
            .map(
              (ctp) => ctp.tags['component-data-frame'] as string | undefined
            )
            .filter((id): id is string => Boolean(id))
        )
      ),
    [ctps]
  )
  const { entries: cdfEntries, forceRefresh: refreshCtpCdfs } =
    useRolodexEntriesWithSyncedRefreshes(
      ctpCdfIds.length > 0
        ? { type: RolodexEntryType.COMPONENT_DATA_FRAME, ids: ctpCdfIds }
        : undefined
    )
  const cdfsById = useMemo(() => {
    const map = new Map<string, GeckoComponentDataFrame>()
    for (const cdf of cdfEntries as ReadonlyArray<GeckoComponentDataFrame>) {
      map.set(cdf.id, cdf)
    }
    return map
  }, [cdfEntries])

  const { markupLayers, forceRefresh: refreshPersistedMarkupLayers } =
    useRolodexEntriesWithSyncedRefreshes(
      component?.id && inspectionId
        ? {
            type: RolodexEntryType.MARKUP_LAYER,
            tags: {
              [RolodexEntryType.COMPONENT]: component.id,
              [RolodexEntryType.INSPECTION]: inspectionId,
            },
          }
        : undefined
    )

  // Flaw markup layers carry no `indication-set` tag and are queried by
  // component+inspection only, so also scope them to the current indications
  // (by their `indication` tag) -- this drops orphaned/cross-ticket flaw layers
  // that would otherwise render and clutter the panel.
  const indicationIdSet = useMemo(() => new Set(indicationIds), [indicationIds])
  const flawMarkupLayers = useMemo(
    () =>
      ltaSetId
        ? [...markupLayers]
            .filter((layer) => {
              const indicationId = getStringTag(
                layer.tags,
                RolodexEntryType.INDICATION
              )
              return (
                isFfsLtaPathMarkupLayer(layer, ltaSetId) &&
                !!indicationId &&
                indicationIdSet.has(indicationId)
              )
            })
            .sort(GeckoMarkupLayer.DefaultSort)
        : [],
    [ltaSetId, markupLayers, indicationIdSet]
  )

  const layers = useMemo(
    () =>
      buildFfsMarkupLayers({
        indications,
        selectedIndicationId,
        ctps,
        rulers,
        cdfsById,
        primaryCdf,
        component,
        flawMarkupLayers,
      }),
    [
      indications,
      selectedIndicationId,
      ctps,
      rulers,
      cdfsById,
      primaryCdf,
      component,
      flawMarkupLayers,
    ]
  )

  useEffect(() => {
    setFfsMarkupLayers(layers)
    return () => setFfsMarkupLayers([])
  }, [layers, setFfsMarkupLayers])

  const forceRefresh = useCallback(
    async () =>
      await Promise.all([
        refreshCtps(),
        refreshRulers(),
        refreshCtpCdfs(),
        refreshPersistedMarkupLayers(),
      ]),
    [refreshCtps, refreshRulers, refreshCtpCdfs, refreshPersistedMarkupLayers]
  )

  return useMemo(() => ({ forceRefresh }), [forceRefresh])
}
